import React, { useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { validationSchema } from "../schema";
import { createNewCoupon } from "../../../../redux/features/CouponSlice";

interface CreateCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const CreateCouponModal: React.FC<CreateCouponModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const dispatch = useAppDispatch();
  const { creating } = useAppSelector((state: RootState) => state.coupons);
  const { user } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    expiry_date: "",
    couponType: "",
    couponValueType: "",
    couponAmount: "",
    couponDiscount: "",
  });

  const [errors, setErrors] = useState({
    expiry_date: "",
    couponType: "",
    couponValueType: "",
    couponAmount: "",
    couponDiscount: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    const payload = {
      ...formData,
      adminUserId: user?.user?.id,
      couponAmount:
        formData.couponValueType === "AMOUNT"
          ? Number(formData.couponAmount)
          : undefined,
      couponDiscount:
        formData.couponValueType === "PERCENTAGE"
          ? Number(formData.couponDiscount)
          : undefined,
    };

    const result = await dispatch(createNewCoupon(payload));
    if (result?.meta?.requestStatus === "fulfilled") {
      toast.success("Coupon created successfully");
      onClose();
      onSave();
    } else {
      toast.error("Failed to create coupon");
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Create Coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            type="submit"
            label="Create"
            onClick={handleSubmit}
            isLoading={creating}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="p-6">
        {/* Expiry Date */}
        <ModalInput
          label="Expiry Date"
          name="expiry_date"
          type="date"
          value={formData.expiry_date}
          onChange={handleChange}
          error={errors.expiry_date}
          required
        />

        {/* Coupon Type */}
        <Dropdown
          label="Coupon Type"
          name="couponType"
          options={[
            { label: "REFERRAL", value: "REFERRAL" },
            { label: "GIVEAWAY", value: "GIVEAWAY" },
          ]}
          placeholder="Select coupon type"
          value={formData.couponType}
          onSelect={(value) => handleSelectChange("couponType", value)}
          error={errors.couponType}
          required
        />

        {/* Value Type */}
        <Dropdown
          label="Value Type"
          name="couponValueType"
          options={[
            { label: "Amount", value: "AMOUNT" },
            { label: "Percentage", value: "PERCENTAGE" },
          ]}
          placeholder="Select value type"
          value={formData.couponValueType}
          onSelect={(value) => handleSelectChange("couponValueType", value)}
          error={errors.couponValueType}
          required
        />

        {/* Conditional Inputs */}
        {formData.couponValueType === "AMOUNT" && (
          <ModalInput
            label="Coupon Amount"
            name="couponAmount"
            type="number"
            value={formData.couponAmount}
            onChange={handleChange}
            placeholder="Enter coupon amount"
            error={errors.couponAmount}
            required
          />
        )}

        {formData.couponValueType === "PERCENTAGE" && (
          <ModalInput
            label="Coupon Discount (%)"
            name="couponDiscount"
            type="number"
            value={formData.couponDiscount}
            onChange={handleChange}
            placeholder="Enter coupon discount"
            error={errors.couponDiscount}
            required
          />
        )}
      </form>
    </CustomModalContainer>
  );
};

export default CreateCouponModal;
